//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { IGetVideoResponse, IVideo } from '@apis/video-processing/videoProcessingApiModel';
import PartialCuid from '@models/PartialCuid';
import VideoId from '@models/VideoId';
import { gatewayFetch, sessionFetch } from '@utils/sessionFetch';

interface IDigestVideoRequest {
  posterUrl: string;
  versions: IVideoVersionRequest[];
}

interface IVideoVersionRequest {
  quality: 'Standard';
  url: string;
}

function gatewayVideoFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  return gatewayFetch('web_gateway', `/api/v1/videos${endpoint}`, init);
}

/**
 * Imports a video to the video processing service and returns its details.
 * This is equivalent to chaining `digestVideo().then(getVideo)`
 * @deprecated This LWS method should not be used in new code! Instead use digestVideo & getVideo.
 */
export async function getVideoByUrl(
  posterUrl: string,
  stdQualityUrl: string,
): Promise<IVideo> {
  const req: IDigestVideoRequest = {
    posterUrl,
    versions: [
      {
        quality: 'Standard',
        url: stdQualityUrl,
      },
    ],
  };
  const res = await sessionFetch(`/api/videos/video`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return res.json();
}

/**
 * Get a video from the video processing service.
 * @param videoId A videoId
 */
export async function getVideo(videoId: VideoId): Promise<IGetVideoResponse> {
  const res = await gatewayVideoFetch(`/video/${videoId}`);
  return res.json();
}

/**
 * Import a video to the video processing service.
 * @param posterUrl Representative frame for the video
 * @param stdQualityUrl standard quality video URL
 */
export async function digestVideo(
  posterUrl: string,
  stdQualityUrl: string,
): Promise<VideoId> {
  const req: IDigestVideoRequest = {
    posterUrl,
    versions: [
      {
        quality: 'Standard',
        url: stdQualityUrl,
      },
    ],
  };
  const res = await gatewayVideoFetch(`/video`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  const resBody: { videoId: VideoId } = await res.json();
  return resBody.videoId;
}
/**
 * Import a video to the video processing service for mute/unmute
 * @param shouldMute Represents whether video to be muted or unmute
 * @param partialCuids represents Items cuids
 */
export async function muteSelectedItems(
  shouldMute: boolean,
  partialCuids: PartialCuid[],
) {
  const res = await gatewayVideoFetch(`/audio`, {
    method: 'POST',
    body: JSON.stringify({
      shouldMute,
      partialCuids,
    }),
  });
  return res;
}

/**
 * Send video cuids to the caption  processing service.
 * @param shouldCaption Caption state for the video
 * @param partialCuids contains partial cuids
 */
export async function processCaptioningOnSelectedItems(
  shouldCaption: boolean,
  partialCuids: PartialCuid[],
) {
  const res = await gatewayVideoFetch(`/caption`, {
    method: 'POST',
    body: JSON.stringify({
      shouldCaption,
      partialCuids,
    }),
  });
  return res.json();
}

/**
 * Get caption status by partial cuids.
 * @param partialCuids contains partial cuids
 */
export async function getCaptionStatuses(partialCuids: PartialCuid[]) {
  const res = await gatewayVideoFetch(`/caption/status`, {
    method: 'POST',
    body: JSON.stringify({
      partialCuids,
    }),
  });
  return res.json();
}
